import React, { useState, useRef } from "react";
import "./CSS/ArticleDetail.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { useLocale, useL, useI18nField } from "../locale/LocaleContext";

const ArticleDetail = ({ article, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);

  // ---- Locale
  const { lang } = useLocale();
  const t = useL();                // for UI strings (i18next / static labels)
  const f = useI18nField();        // for Firestore content fields: { en, fr } or plain

  // Optional helpers: date/number formatting by locale
  const nf = new Intl.NumberFormat(lang);
  const df = new Intl.DateTimeFormat(lang, { dateStyle: "medium" });

  // ---- Touch refs
  const touchStart = useRef(0);
  const touchMove = useRef(0);

  const imgs = article?.imageUrl || [];
  if (!article || imgs.length === 0) return null;

  const goToIndex = (index) => setCurrentIndex(index);
  const goToPrevious = () =>
    setCurrentIndex((i) => (i === 0 ? imgs.length - 1 : i - 1));
  const goToNext = () =>
    setCurrentIndex((i) => (i === imgs.length - 1 ? 0 : i + 1));

  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchMove.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    if (touchStart.current - touchMove.current > 50) goToNext();
    else if (touchStart.current - touchMove.current < -50) goToPrevious();
  };

  const localizedTitle = f(article, "title"); // falls back to EN/plain if FR missing

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      message: formData.get("message"),
      subject: t({
        en: `Inquiry about ${localizedTitle || article.title || ""}`,
        fr: `Demande au sujet de ${localizedTitle || article.title || ""}`,
      }),
    };

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to send email");
      alert(t({ en: "Your inquiry has been sent!", fr: "Votre demande a été envoyée !" }));
      setShowContactForm(false);
    } catch (error) {
      console.error("Error sending email:", error);
      alert(t({ en: "Error sending email. Please try again later.", fr: "Erreur d’envoi. Réessayez plus tard." }));
    }
  };

  return (
    <div className="article-detail-backdrop">
      <div className="article-detail-content items-center justify-center w-full max-w-6xl p-5 bg-white rounded-lg shadow-lg max-h-[1200px] ">
        <button
          onClick={onClose}
          className="close-button absolute top-2.5 right-2.5 text-2xl"
          aria-label={t({ en: "Close", fr: "Fermer" })}
        >
          X
        </button>

        <div className="slider-container w-full relative overflow-hidden">
          <div
            className="slider w-full relative"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="image-container flex justify-center md:justify-start items-center relative w-full h-auto overflow-hidden">
              <div className="aspect-w-16 aspect-h-9 w-full max-w-screen-lg">
                <div
                  className="slide-area left absolute top-1/2 transform -translate-y-1/2 left-4 z-10 cursor-pointer"
                  onClick={goToPrevious}
                  aria-label={t({ en: "Previous image", fr: "Image précédente" })}
                >
                  <span className="arrow">&#9664;</span>
                </div>

                <img
                  src={imgs[currentIndex]}
                  alt={
                    f(article, "heroAlt") ||
                    t({
                      en: `Slide ${currentIndex + 1}`,
                      fr: `Diapositive ${currentIndex + 1}`,
                    })
                  }
                  className="object-contain w-full h-full"
                />

                <div
                  className="slide-area right absolute top-1/2 transform -translate-y-1/2 right-4 z-10 cursor-pointer"
                  onClick={goToNext}
                  aria-label={t({ en: "Next image", fr: "Image suivante" })}
                >
                  <span className="arrow">&#9654;</span>
                </div>
              </div>
            </div>
          </div>

          <div className="dot-container flex justify-center p-2.5" role="tablist">
            {imgs.map((img, index) => (
              <span
                key={index}
                role="tab"
                aria-selected={index === currentIndex}
                aria-label={t({
                  en: `Go to image ${index + 1}`,
                  fr: `Aller à l’image ${index + 1}`,
                })}
                className={`dot ${index === currentIndex ? "active" : ""}`}
                onClick={() => goToIndex(index)}
                style={{
                  height: "10px",
                  width: "10px",
                  backgroundColor: index === currentIndex ? "#717171" : "#bbb",
                }}
              />
            ))}
          </div>
        </div>

        <div className="description-container">
          <h2 className="text-xl font-semibold mb-2">{localizedTitle || ""}</h2>

          <div style={{ maxHeight: "400px", overflowY: "auto", paddingRight: "6px" }}>
            {/* DB-backed fields via useI18nField() */}
            <p className="mb-4">{f(article, "manufacture_description")}</p>
            <p className="mb-4">{f(article, "product_description")}</p>

            {/* If you store rich text/HTML for bodyHtml, you can render it safely if sanitized */}
            {/* <div dangerouslySetInnerHTML={{ __html: f(article, "bodyHtml") }} /> */}
          </div>

          <p>{f(article, "description")}</p>

          <button
            onClick={() => setShowContactForm((v) => !v)}
            className="mt-5 px-5 py-2.5 text-base text-black bg-gray-300 rounded cursor-pointer hover:bg-gray-600"
          >
            {t({ en: "Contact Us About This Article", fr: "Nous contacter à propos de cet article" })}
          </button>

          {showContactForm && (
            <div className="mt-4 max-h-[300px] overflow-y-auto border border-gray-200 rounded p-2">
              <form onSubmit={handleSubmit} className="flex flex-col w-full">
                <input
                  type="text"
                  name="name"
                  placeholder={t({ en: "Your Name", fr: "Votre nom" })}
                  required
                  className="form-input mt-2.5 p-2 border rounded border-gray-300"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t({ en: "Your Email", fr: "Votre e-mail" })}
                  required
                  className="form-input mt-2.5 p-2 border rounded border-gray-300"
                />
                <textarea
                  name="message"
                  placeholder={t({ en: "Your Message", fr: "Votre message" })}
                  required
                  className="form-textarea mt-2.5 p-2 border rounded border-gray-300"
                />
                <button
                  type="submit"
                  className="form-submit-button mt-2.5 px-5 py-2.5 bg-gray-500 text-white rounded cursor-pointer hover:bg-gray-700"
                >
                  {t({ en: "Send", fr: "Envoyer" })}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* <p className="mt-3 text-sm text-gray-500">
          {t({ en: "Updated", fr: "Mis à jour" })}: {df.format(new Date(article.updatedAt))}
        </p> */}
        {/* <p>{t({ en: "Price", fr: "Prix" })}: {nf.format(article.price)}</p> */}
      </div>
    </div>
  );
};

export default ArticleDetail;
